import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { isNativeApp } from "./native";
import en from "../locales/en.json";
import de from "../locales/de.json";
import es from "../locales/es.json";
import fr from "../locales/fr.json";
import it from "../locales/it.json";

export type Locale = "en" | "de" | "es" | "fr" | "it";

export const LOCALES: Locale[] = ["en", "de", "es", "fr", "it"];

/** Native-language display names (for the language picker + settings row). */
export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  it: "Italiano",
};

/** Language for backend calls (emails, AI estimates) — those exist in en/de
    only, so every other UI locale falls back to English. */
export function apiLang(locale: Locale): "en" | "de" {
  return locale === "de" ? "de" : "en";
}

const STORAGE_KEY = "lean_locale";
const DICTS: Record<Locale, Record<string, string>> = {
  en: en as Record<string, string>,
  de: de as Record<string, string>,
  es: es as Record<string, string>,
  fr: fr as Record<string, string>,
  it: it as Record<string, string>,
};

function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as string[]).includes(v);
}

function detectInitial(): Locale {
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    const prefix = navigator.language.slice(0, 2).toLowerCase();
    if (isLocale(prefix)) return prefix;
  }
  return "en";
}

interface Ctx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LocaleCtx = createContext<Ctx | null>(null);

/** A language the person PICKED beats every heuristic, forever. */
const EXPLICIT_KEY = "caloly.locale_explicit";

/** IP country → language, for first-visit web visitors (owner decision: a
 *  German visitor should land on the German page). Conservative map — only
 *  countries whose store language is unambiguous; everyone else keeps the
 *  browser-language guess from detectInitial(). */
const COUNTRY_LOCALE: Record<string, Locale> = {
  DE: "de", AT: "de", CH: "de", LI: "de",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", UY: "es", EC: "es",
  FR: "fr", BE: "fr", MC: "fr",
  IT: "it", SM: "it",
};

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitial());

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, locale);
    }
  }, [locale]);

  // Geo pass, web only (the app follows the device language): one cheap edge
  // call, applied only until the person explicitly picks a language.
  useEffect(() => {
    if (isNativeApp()) return;
    try { if (localStorage.getItem(EXPLICIT_KEY)) return; } catch { return; }
    let cancelled = false;
    fetch("/api/geo")
      .then((r) => r.json() as Promise<{ country: string | null }>)
      .then(({ country }) => {
        const geo = country ? COUNTRY_LOCALE[country] : undefined;
        if (!cancelled && geo) setLocaleState(geo);
      })
      .catch(() => { /* the browser-language guess stands */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setLocale(next: Locale) {
    setLocaleState(next);
    try { localStorage.setItem(EXPLICIT_KEY, "1"); } catch { /* fine */ }
  }

  function t(key: string, vars?: Record<string, string | number>): string {
    // Fall back to English for keys a locale doesn't have (yet).
    const v = DICTS[locale][key] ?? DICTS.en[key];
    if (typeof v !== "string") return key;
    if (!vars) return v;
    return Object.entries(vars).reduce(
      (s, [k, val]) => s.replaceAll(`{${k}}`, String(val)),
      v,
    );
  }

  return (
    <LocaleCtx.Provider value={{ locale, setLocale, t }}>{children}</LocaleCtx.Provider>
  );
}

export function useT(): Ctx["t"] {
  const ctx = useContext(LocaleCtx);
  if (!ctx) throw new Error("useT must be used inside LocaleProvider");
  return ctx.t;
}

export function useLocale(): { locale: Locale; setLocale: (l: Locale) => void } {
  const ctx = useContext(LocaleCtx);
  if (!ctx) throw new Error("useLocale must be used inside LocaleProvider");
  return { locale: ctx.locale, setLocale: ctx.setLocale };
}
